import { getToken, getCompany } from "../../../../shared/data/storage/authStorage";
import {
  DebitNoteDataEntity,
  DebitNoteListStatus,
} from "../../domain/entities/DebitNoteEntity";
import { API_BASE_URL } from "../../../../config/api";

// HTTP Status Codes
const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500,
};

// API Error Class
class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

export interface DebitNotesQueryParams {
  limit?: number;
  offset?: number;
  status?: DebitNoteListStatus;
  search?: string;
  date_from?: string;
  date_to?: string;
  sort_by?: "created_at" | "updated_at" | "document_number" | "status";
  sort_order?: "asc" | "desc";
}

/**
 * Fetch debit notes through the unified list endpoint.
 */
export const fetchDebitNotes = async (
  params: DebitNotesQueryParams = {}
): Promise<any> => {
  const token = getToken();
  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }

  const queryParams = new URLSearchParams();
  if (params.limit !== undefined) {
    queryParams.append("limit", Math.min(params.limit, 100).toString());
  }
  if (params.offset !== undefined) {
    queryParams.append("offset", params.offset.toString());
  }
  if (params.status) {
    queryParams.append("status", params.status);
  }
  if (params.search) {
    queryParams.append("search", params.search);
  }
  if (params.date_from) {
    queryParams.append("date_from", params.date_from);
  }
  if (params.date_to) {
    queryParams.append("date_to", params.date_to);
  }
  if (params.sort_by) {
    queryParams.append("sort_by", params.sort_by);
  }
  if (params.sort_order) {
    queryParams.append("sort_order", params.sort_order);
  }

  const url = `${API_BASE_URL}/api/debit-notes${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new APIError(
      errorData.message || `HTTP error! status: ${response.status}`,
      response.status,
      errorData
    );
  }

  return response.json();
};

/**
 * Update Debit Note JSON
 * Updates the JSON field of a debit note (for auto-save)
 */
export const updateDebitNoteJson = async (
  debitNoteId: number,
  debitNoteJson: DebitNoteDataEntity
): Promise<any> => {
  const token = getToken();

  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/debit-notes/${debitNoteId}/update-json`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        debit_note_id: debitNoteId,
        debit_note_json: debitNoteJson,
      }),
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw new APIError(
      errorData.message || "Failed to update debit note JSON",
      response.status,
      errorData
    );
  }

  return response.json();
};

/**
 * Create Debit Note in Zoho Books
 * Creates a new debit note in the database and pushes it to Zoho Books
 * If debitNoteId is provided, updates the existing debit note instead of creating a new one
 * Note: For updating existing debit notes, use updateZohoDebitNote()
 * @param debitNoteJson - Debit note data
 * @param pdfUrl - Optional PDF URL
 * @param debitNoteId - Optional existing debit note ID (for updating drafts)
 */
export const createZohoDebitNote = async (
  debitNoteJson: DebitNoteDataEntity,
  pdfUrl?: string,
  debitNoteId?: number
): Promise<any> => {
  const token = getToken();
  const companyData = getCompany();

  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  const requestBody: any = {
    debit_note_json: debitNoteJson,
    pdf_url: pdfUrl,
    company_gst_enabled: companyData?.companyGstEnabled,
  };

  // Include debit_note_id if provided (for updating existing drafts)
  if (debitNoteId) {
    requestBody.debit_note_id = debitNoteId;
  }

  const response = await fetch(
    `${API_BASE_URL}/api/debit-notes/push-to-zoho`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw new APIError(
      errorData.message || "Failed to create debit note in Zoho",
      response.status,
      errorData
    );
  }

  return response.json();
};

/**
 * Update Debit Note in Zoho Books
 */
export const updateZohoDebitNote = async (
  debitNoteId: number,
  debitNoteJson: DebitNoteDataEntity
): Promise<any> => {
  const token = getToken();
  const companyData = getCompany();

  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/debit-notes/${debitNoteId}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        debit_note_id: debitNoteId,
        debit_note_json: debitNoteJson,
        company_gst_enabled: companyData?.companyGstEnabled,
      }),
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw new APIError(
      errorData.message || "Failed to update debit note in Zoho",
      response.status,
      errorData
    );
  }

  return response.json();
};

/**
 * Reject Debit Note
 */
export const rejectDebitNote = async (debitNoteId: number): Promise<any> => {
  const token = getToken();

  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/debit-notes/${debitNoteId}/reject`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw new APIError(
      errorData.message || "Failed to reject debit note",
      response.status,
      errorData
    );
  }

  return response.json();
};

/**
 * Approve Debit Note
 */
export const approveDebitNote = async (debitNoteId: number): Promise<any> => {
  const token = getToken();

  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/debit-notes/${debitNoteId}/approve`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw new APIError(
      errorData.message || "Failed to approve debit note",
      response.status,
      errorData
    );
  }

  return response.json();
};

/**
 * Get Debit Note by ID
 * Fetches a single debit note with all its data including status and zoho_id
 */
export const getDebitNoteById = async (debitNoteId: number): Promise<any> => {
  const token = getToken();

  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  const response = await fetch(
    `${API_BASE_URL}/api/debit-notes/${debitNoteId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw new APIError(
      errorData.message || "Failed to fetch debit note",
      response.status,
      errorData
    );
  }

  return response.json();
};

/**
 * Save Debit Note As Draft
 * Creates a new debit note with draft status (0)
 */
export const saveDebitNoteAsDraft = async (
  debitNoteJson: DebitNoteDataEntity,
  options?: {
    jobid?: string | null;
    folder_id?: string | null;
    company_id?: number | null;
    gst_rates?: any;
    gst_types?: any;
    is_tds_applied?: number;
    tds_tax_id?: string | null;
    tds_tax_amount?: number | null;
    tds_tax_percentage?: number | null;
    tds_tax_name?: string | null;
    tds_section?: string | null;
  }
): Promise<any> => {
  const token = getToken();

  if (!token) {
    throw new APIError("No authentication token found", 401);
  }

  const response = await fetch(`${API_BASE_URL}/api/debit-notes/save-draft`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      json: debitNoteJson,
      jobid: options?.jobid || null,
      folder_id: options?.folder_id || null,
      company_id: options?.company_id || null,
      gst_rates: options?.gst_rates || null,
      gst_types: options?.gst_types || null,
      is_tds_applied: options?.is_tds_applied || 0,
      tds_tax_id: options?.tds_tax_id || null,
      tds_tax_amount: options?.tds_tax_amount || null,
      tds_tax_percentage: options?.tds_tax_percentage || null,
      tds_tax_name: options?.tds_tax_name || null,
      tds_section: options?.tds_section || null,
    }),
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw new APIError(
      errorData.message || "Failed to save debit note as draft",
      response.status,
      errorData
    );
  }

  return response.json();
};

/**
 * Fetch debit note counts for dashboard cards.
 */
export const fetchDebitNoteCounts = async (params?: {
  date_from?: string;
  date_to?: string;
}): Promise<{
  pending_count: number;
  posted_count: number;
  rejected_count: number;
  under_review_count: number;
}> => {
  try {
    const token = getToken();
    if (!token) {
      throw new APIError(
        "No authentication token found",
        HTTP_STATUS.UNAUTHORIZED
      );
    }

    const queryParams = new URLSearchParams();
    if (params?.date_from) {
      queryParams.append("date_from", params.date_from);
    }
    if (params?.date_to) {
      queryParams.append("date_to", params.date_to);
    }

    const response = await fetch(
      `${API_BASE_URL}/api/debit-notes/counts${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData
      );
    }

    const data = await response.json();
    return {
      pending_count: data.data?.pending_count || 0,
      posted_count: data.data?.posted_count || 0,
      rejected_count: data.data?.rejected_count || 0,
      under_review_count: data.data?.under_review_count || 0,
    };
  } catch (error) {
    console.error("Error fetching debit note counts:", error);
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};
